import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Grid,
  Box,
  Button,
  Typography,
  Chip,
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  CircularProgress,
  TextField,
  Tooltip,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import PrintIcon from "@mui/icons-material/Print";
import SmsIcon from "@mui/icons-material/Sms";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneIcon from "@mui/icons-material/Phone";
import MapIcon from "@mui/icons-material/Map";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EventIcon from "@mui/icons-material/Event";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import { fmtDriverName } from "../../../Core/utils/nameUtils";
import {
  IInvoice,
  IDriver,
} from "../../../Api/Interface/api.interface";
import { useGetAllInvoiceQuery } from "../../../Api/attoDeskApi";
import { usePermissions } from "../../../Core/Permissions";

// ── Interfaces ──────────────────────────────────────────────────────────────────
interface Payment {
  id: number;
  transaction_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  payment_mode: string;
  description: string;
  file_path?: string;
  created_at: string;
  updated_at: string;
  is_return: number;
  is_deleted: number;
  InvoiceNo?: string;
}
interface Driver { id: number; driverFirstName: string; driverLastName: string; driverUserRole: string; }
interface Deposit { id: number; amount: string; user_id: number; created_at: string; date: string; is_approved: boolean; remarks: string; }
interface UserInfo { id: number; username: string; token: string; user_id: number; }

// ── Helpers ─────────────────────────────────────────────────────────────────────
const fmt = (n: number) => `$${n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const TODAY = new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" });

/** Tiny SVG sparkline rendered inline */
const Sparkline: React.FC<{ data: number[]; color: string; height?: number }> = ({ data, color, height = 36 }) => {
  const w = 80;
  const h = height;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (v / max) * h;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={w} height={h} style={{ overflow: "visible" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
    </svg>
  );
};

// ── KPI Card ─────────────────────────────────────────────────────────────────────
interface KpiProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  gradient: string[];
  sparkData: number[];
  onClick?: () => void;
}
const KpiCard: React.FC<KpiProps> = ({ title, value, icon, gradient, sparkData, onClick }) => {
  const prev = sparkData.slice(0, -1);
  const last = sparkData[sparkData.length - 1] ?? 0;
  const secondLast = sparkData[sparkData.length - 2] ?? 0;
  const isUp = last >= secondLast;
  const pct = secondLast > 0 ? Math.abs(((last - secondLast) / secondLast) * 100).toFixed(1) : null;

  return (
    <Box
      onClick={onClick}
      sx={{
        background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
        borderRadius: 3,
        p: 2.5,
        cursor: onClick ? "pointer" : "default",
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        transition: "transform 0.18s, box-shadow 0.18s",
        "&:hover": onClick ? { transform: "translateY(-3px)", boxShadow: "0 8px 28px rgba(0,0,0,0.18)" } : {},
        position: "relative",
        overflow: "hidden",
        height: "100%",
        minHeight: 120,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Background icon */}
      <Box sx={{ position: "absolute", right: -8, top: -8, opacity: 0.12, fontSize: 80 }}>
        {icon}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.8)", textTransform: "uppercase", letterSpacing: 0.8, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
            {value}
          </Typography>
          {pct && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.75 }}>
              {isUp
                ? <TrendingUpIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.9)" }} />
                : <TrendingDownIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }} />}
              <Typography sx={{ fontSize: 11, color: isUp ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                {pct}% vs last month
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 0.5 }}>
          <Sparkline data={sparkData} color="rgba(255,255,255,0.75)" />
        </Box>
      </Box>
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────────
const DashboardList: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const { hasPermission, roleName } = usePermissions();
  const can = (mod: string) => hasPermission(mod, "can_view");
  const isDriver = roleName === "Driver";
  const API_URL = process.env.REACT_APP_API_URL;
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;

  // ── State ────────────────────────────────────────────────────────────────────
  const [payments, setPayments]           = useState<Payment[]>([]);
  const [team, setTeam]                   = useState<Driver[]>([]);
  const [deposit, setDeposit]             = useState<Deposit[]>([]);
  const [userInfo, setUserInfo]           = useState<UserInfo | null>(null);
  const [totalClients, setTotalClients]   = useState(0);
  const [monthlyPayments, setMonthlyPayments] = useState<number[]>(Array(12).fill(0));
  const [monthlyInvoiceCounts, setMonthlyInvoiceCounts] = useState<number[]>(Array(12).fill(0));
  const [todaysBookings, setTodaysBookings] = useState<IInvoice[]>([]);
  const [insightIdx, setInsightIdx]       = useState(0);
  const [approving, setApproving]         = useState<number | null>(null);
  const [smsModal, setSmsModal]           = useState<{ booking: IInvoice; type: "client" | "driver" } | null>(null);
  const [smsMessage, setSmsMessage]       = useState("");
  const [smsSending, setSmsSending]       = useState(false);
  const [smsSent, setSmsSent]             = useState<Set<string>>(new Set());
  const [printTrip, setPrintTrip]         = useState<IInvoice | null>(null);
  const [selectedTrips, setSelectedTrips] = useState<Set<number>>(new Set());
  const [bulkSending, setBulkSending]     = useState(false);
  const [revenueByMethod, setRevenueByMethod] = useState<{ labels: string[]; data: number[] }>({ labels: [], data: [] });

  // ── RTK Invoice query ────────────────────────────────────────────────────────
  const { data, isLoading } = useGetAllInvoiceQuery({
    searchText: "", id: "", quoteWorkOrderCustomerId: "", quoteWorkOrderCustomerProjectId: "",
  });
  const invoices: IInvoice[] = useMemo(() => Array.isArray(data?.data) ? (data?.data as IInvoice[]) : [], [data]);

  // ── Derived KPI values ───────────────────────────────────────────────────────
  const totalRevenue  = useMemo(() => invoices.reduce((s, i) => s + (parseFloat(i.quoteWorkOrderSaleGrandTotal) || 0), 0), [invoices]);
  const totalPaid     = useMemo(() => invoices.reduce((s, i) => s + (Number(i.amount_paid) || 0), 0), [invoices]);
  const outstanding   = useMemo(() => Math.max(0, totalRevenue - totalPaid), [totalRevenue, totalPaid]);
  const totalInvoices = invoices.length;

  // ── Payment method breakdown ─────────────────────────────────────────────────
  const paymentMethods = ["Cash","Credit","Debit","E-Transfer","Online banking","Pre authorization","Bank deposit"];
  const paymentMethodTotals = useMemo(() => {
    const t: Record<string, number> = {};
    payments.forEach((p) => { t[p.payment_method] = (t[p.payment_method] || 0) + Number(p.amount); });
    return t;
  }, [payments]);

  const topMethod = useMemo(() => {
    let top = "", max = 0;
    Object.entries(paymentMethodTotals).forEach(([k, v]) => { if (v > max) { max = v; top = k; } });
    return top || "—";
  }, [paymentMethodTotals]);

  const overdueCount = useMemo(() => invoices.filter((i) => (Number(i.due_amount) || 0) > 0).length, [invoices]);
  const recentPayments = useMemo(() => [...payments].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 8), [payments]);

  // ── Smart insights ───────────────────────────────────────────────────────────
  const insights = useMemo(() => {
    const list: string[] = [];
    const thisMonth = new Date().getMonth();
    const lastMonthPay = monthlyPayments[thisMonth - 1] || 0;
    const thisMonthPay = monthlyPayments[thisMonth] || 0;
    if (can("reports") && lastMonthPay > 0) {
      const diff = ((thisMonthPay - lastMonthPay) / lastMonthPay * 100).toFixed(1);
      list.push(Number(diff) >= 0
        ? `🔥 Revenue increased ${diff}% this month`
        : `📉 Revenue down ${Math.abs(Number(diff))}% vs last month`);
    }
    if (can("invoices") && overdueCount > 0) list.push(`⚠️ ${overdueCount} invoice${overdueCount > 1 ? "s" : ""} with outstanding balance`);
    if (can("trips") && todaysBookings.length > 0) list.push(`🚍 ${todaysBookings.length} trip${todaysBookings.length > 1 ? "s" : ""} scheduled today`);
    if (can("reports") && topMethod !== "—") list.push(`💡 ${topMethod} is the most used payment method`);
    if (can("customers") && totalClients > 0) list.push(`👥 ${totalClients} active clients on record`);
    return list;
  }, [monthlyPayments, overdueCount, todaysBookings, topMethod, totalClients, hasPermission]);

  // ── Rotate insight every 4s ──────────────────────────────────────────────────
  useEffect(() => {
    if (insights.length <= 1) return;
    const t = setInterval(() => setInsightIdx((i) => (i + 1) % insights.length), 4000);
    return () => clearInterval(t);
  }, [insights.length]);

  // ── Today's bookings ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (isLoading || !data?.data) return;
    // Debug: log first invoice to check actual date field formats
    if (invoices.length > 0) {
      const sample = invoices[0];
      console.log("[TodayTrips] TODAY=", TODAY, "| sample issueDate=", (sample as any).quoteWorkOrderIssueDate, "| sale[0].pickups[0].dateTime=", (sample.quoteWorkOrderSale?.[0] as any)?.pickups?.[0]?.dateTime);
    }
    // Resolve the trip date for an invoice the same way the calendar does:
    //  1. First pickup's dateTime on the first sale row (canonical source)
    //  2. Legacy departureDateTime on the sale row
    //  3. Work-order issue date as a last-resort fallback
    // Using the same field lets the dashboard "Today's Trips" count match the
    // calendar's tile count for the same day.
    const tripDateMatches = (inv: any, today: string): boolean => {
      const sales: any[] = Array.isArray(inv.quoteWorkOrderSale) ? inv.quoteWorkOrderSale : [];
      for (const s of sales) {
        const pickupDt = s?.pickups?.[0]?.dateTime;
        if (pickupDt) {
          const p = moment(pickupDt);
          if (p.isValid() && p.format("YYYY-MM-DD") === today) return true;
        }
        const dep = s?.departureDateTime;
        if (dep) {
          const p = moment(dep);
          if (p.isValid() && p.format("YYYY-MM-DD") === today) return true;
        }
      }
      const issueDate = inv.quoteWorkOrderIssueDate;
      if (issueDate) {
        const p = moment(issueDate);
        return p.isValid() && p.format("YYYY-MM-DD") === today;
      }
      return false;
    };

    const todaysList = invoices.filter((inv) => tripDateMatches(inv, TODAY));
    setTodaysBookings(todaysList);
    // eslint-disable-next-line no-console
    console.log("DASHBOARD TRIPS:", todaysList);

    const counts = Array(12).fill(0);
    invoices.forEach((inv) => { if (inv.created_at) counts[new Date(inv.created_at).getMonth()]++; });
    setMonthlyInvoiceCounts(counts);
  }, [isLoading, data]);

  // ── Data fetching ────────────────────────────────────────────────────────────
  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem("token") || ""}`, "Content-Type": "application/json" };

    axios.get(`${API_URL}/monthly-payments`).then((r) => {
      const arr = Array(12).fill(0);
      r.data.forEach((p: { month: number; total_payments: any }) => { arr[p.month - 1] = parseFloat(String(p.total_payments)) || 0; });
      setMonthlyPayments(arr);
    }).catch(() => {});

    axios.get(`${API_URL}/view-team`).then((r) => {
      setTeam((r.data.data as Driver[]).filter((d) => d.driverUserRole === "manager"));
    }).catch(() => {});

    axios.get(`${API_URL}/view-deposit`).then((r) => setDeposit(r.data)).catch(() => {});

    axios.post(`${API_URL}/user/userInfo`, {}, { headers }).then((r) => setUserInfo(r.data.data)).catch(() => {});

    axios.post(`${API_URL}/customer/getAll`, {}, { headers }).then((r) => {
      setTotalClients((r.data.data as any[]).filter((c) => c.isDelete === 0).length);
    }).catch(() => {});

    axios.get(`${API_URL}/payments`).then((r) => {
      setPayments((r.data as Payment[]).filter((p) => p.is_deleted === 0));
    }).catch(() => {});

    axios.get(`${API_URL}/dashboard/revenue-by-payment`, { headers }).then((r) => {
      if (r.data && Array.isArray(r.data.labels) && Array.isArray(r.data.data)) {
        setRevenueByMethod({ labels: r.data.labels, data: r.data.data });
      }
    }).catch(() => {});
  }, [API_URL]);

  // ── Deposit approval ─────────────────────────────────────────────────────────
  const handleApproveDeposit = async (id: number) => {
    setApproving(id);
    try {
      await axios.put(`${API_URL}/deposits/${id}/approve`, { approved_by: userInfo?.user_id });
      setDeposit((prev) => prev.map((d) => d.id === id ? { ...d, is_approved: true } : d));
      showMessage("Deposit approved");
    } catch { showErrorMessage("Failed to approve"); }
    finally { setApproving(null); }
  };

  // ── Trip action helpers ──────────────────────────────────────────────────────
  const getTripInfo = (booking: IInvoice) => {
    const sale = booking.quoteWorkOrderSale?.[0] as any;
    const client = [booking.customer?.customerFirstName, booking.customer?.customerLastName].filter(Boolean).join(" ") || booking.customer?.customerCompanyName || "—";
    const clientPhone = booking.customer?.customerMobileNum || "";
    const pickup  = sale?.pickups?.[0]  ? `${sale.pickups[0].address}, ${sale.pickups[0].city}`    : (sale?.transferTypeFrom || "—");
    const dropoff = sale?.dropoffs?.[0] ? `${sale.dropoffs[0].address}, ${sale.dropoffs[0].city}` : (sale?.transferTypeTo   || "—");
    const parsedDep = sale?.departureDateTime ? moment(sale.departureDateTime) : null;
    const time = parsedDep?.isValid() ? parsedDep.format("hh:mm A") : "—";
    const date = parsedDep?.isValid() ? parsedDep.format("dddd, MMMM D") : "Today";
    const drivers: IDriver[] = (booking.assign_driver?.assignedDrivers as IDriver[]) || [];
    const driverNames = drivers.map((d) => fmtDriverName(d)).filter(n => n !== "—").join(", ") || "—";
    const driverPhone = drivers[0]?.driverMobileNum || "";
    const passengers  = (sale?.numberOfPassengers as number | null) || null;
    return { client, clientPhone, pickup, dropoff, time, date, drivers, driverNames, driverPhone, passengers };
  };

  const buildClientMessage = (booking: IInvoice) => {
    const { client, pickup, dropoff, time, date, driverNames, driverPhone } = getTripInfo(booking);
    return `Hi ${client}, your trip on ${date} at ${time} is confirmed.\nPickup: ${pickup}\nDrop-off: ${dropoff}\nDriver: ${driverNames}${driverPhone ? ` | ${driverPhone}` : ""}\nThank you - TJBus`;
  };

  const buildDriverMessage = (booking: IInvoice) => {
    const { client, clientPhone, pickup, dropoff, time, date, passengers } = getTripInfo(booking);
    return `New Trip Reminder\n\nDate: ${date}\nPickup Time: ${time}\n\nPickup: ${pickup}\nDrop-off: ${dropoff}\n\nClient: ${client}${clientPhone ? ` | ${clientPhone}` : ""}${passengers ? `\nPassengers: ${passengers}` : ""}\n\nPlease arrive 10 mins before pickup time.\n- TJBus`;
  };

  const openSmsModal = (booking: IInvoice, type: "client" | "driver") => {
    setSmsMessage(type === "client" ? buildClientMessage(booking) : buildDriverMessage(booking));
    setSmsModal({ booking, type });
  };

  const handleSendSms = async () => {
    if (!smsModal) return;
    const { booking, type } = smsModal;
    const { clientPhone, drivers } = getTripInfo(booking);
    const phones = type === "client"
      ? [clientPhone].filter(Boolean)
      : drivers.map((d) => d.driverMobileNum).filter(Boolean);
    if (phones.length === 0) { showErrorMessage("No phone number found for this " + type); return; }
    setSmsSending(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token") || ""}` };
      await Promise.all(phones.map((phone) =>
        axios.post(`${API_URL}/send-text`, { phone_no: phone, message: smsMessage, customer_id: booking.quoteWorkOrderCustomerId }, { headers })
      ));
      showMessage("SMS sent successfully");
      setSmsSent((prev) => new Set(prev).add(`${booking.work_order_id}_${type}`));
      setSmsModal(null);
    } catch { showErrorMessage("Failed to send SMS"); }
    finally { setSmsSending(false); }
  };

  const handleBulkSmsDrivers = async () => {
    if (selectedTrips.size === 0) { showErrorMessage("Select at least one trip"); return; }
    setBulkSending(true);
    const headers = { Authorization: `Bearer ${localStorage.getItem("token") || ""}` };
    let sent = 0;
    for (const booking of todaysBookings.filter((b) => selectedTrips.has(b.work_order_id))) {
      const { drivers } = getTripInfo(booking);
      const msg = buildDriverMessage(booking);
      for (const d of drivers.filter((dr) => dr.driverMobileNum)) {
        try { await axios.post(`${API_URL}/send-text`, { phone_no: d.driverMobileNum, message: msg }, { headers }); sent++; } catch {}
      }
    }
    setBulkSending(false);
    showMessage(`Bulk SMS sent to ${sent} driver${sent !== 1 ? "s" : ""}`);
    setSelectedTrips(new Set());
  };

  const toggleTrip = (id: number) =>
    setSelectedTrips((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  // ── Charts ───────────────────────────────────────────────────────────────────
  const revenueChartOptions: any = {
    chart: { type: "areaspline", height: 280, backgroundColor: "transparent", style: { fontFamily: "inherit" } },
    title: { text: "" },
    xAxis: { categories: MONTHS, lineColor: "#e2e8f0", tickColor: "transparent", labels: { style: { color: "#94a3b8", fontSize: "11px" } } },
    yAxis: [
      { title: { text: "" }, gridLineColor: "#f1f5f9", labels: { style: { color: "#94a3b8", fontSize: "11px" }, format: "${value:,.0f}" } },
      { title: { text: "" }, opposite: true, gridLineColor: "transparent", labels: { style: { color: "#2f3891", fontSize: "11px" } } },
    ],
    tooltip: { shared: true, backgroundColor: "#1e293b", borderColor: "transparent", style: { color: "#fff" }, valuePrefix: "$" },
    legend: { enabled: true, align: "right", verticalAlign: "top", itemStyle: { color: "#64748b", fontWeight: "500", fontSize: "11px" } },
    credits: { enabled: false },
    plotOptions: { areaspline: { fillOpacity: 0.12 } },
    series: [
      { type: "areaspline", name: "Payments", data: monthlyPayments, color: "#2f3891", yAxis: 0 },
      { type: "spline", name: "Invoices", data: monthlyInvoiceCounts, color: "#10b981", yAxis: 1 },
    ],
  };

  const pieHasData = revenueByMethod.labels.length > 0;

  const methodChartOptions: any = useMemo(() => ({
    chart: { type: "pie", height: 280, backgroundColor: "transparent", style: { fontFamily: "inherit" } },
    title: { text: "" },
    tooltip: { backgroundColor: "#1e293b", borderColor: "transparent", style: { color: "#fff" }, pointFormat: "<b>{point.name}</b>: ${point.y:,.2f} ({point.percentage:.1f}%)" },
    legend: { enabled: true, align: "right", verticalAlign: "middle", layout: "vertical", itemStyle: { color: "#64748b", fontWeight: "500", fontSize: "11px" } },
    credits: { enabled: false },
    plotOptions: {
      pie: {
        innerSize: "50%",
        borderWidth: 0,
        dataLabels: { enabled: false },
        showInLegend: true,
        colors: ["#2f3891","#10b981","#ec4899","#14b8a6","#f59e0b","#3b82f6","#f97316"],
      },
    },
    series: [{
      type: "pie",
      name: "Revenue",
      data: revenueByMethod.labels.map((label, i) => ({
        name:  label,
        y:     revenueByMethod.data[i] ?? 0,
      })),
    }],
  }), [revenueByMethod]);

  // ── Pending deposits ─────────────────────────────────────────────────────────
  const pendingDeposits = useMemo(() =>
    team
      .filter((p) => p.id !== userInfo?.user_id)
      .flatMap((p) =>
        deposit.filter((d) => d.user_id === p.id && !d.is_approved).map((d) => ({ ...d, driver: p }))
      ),
    [team, deposit, userInfo]
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  // Drivers get an operational dashboard built entirely from invoices already
  // scoped to them by the backend. Sections: status header, today's trips,
  // upcoming trips, alerts. No payment data, no admin actions.
  if (isDriver) {
    type DriverTrip = {
      id: string;
      workOrderId: string;
      pickupDateTime: string;
      dropoffDateTime: string | null;
      clientName: string;
      clientPhone: string;
      from: string;
      to: string;
      status: string;
    };

    const allTrips: DriverTrip[] = [];
    invoices.forEach((inv: any) => {
      const customer = inv?.customer ?? {};
      const clientName =
        customer?.customerCompanyName ||
        `${customer?.customerFirstName || ""} ${customer?.customerLastName || ""}`.trim() ||
        "—";
      const clientPhone = customer?.customerMobileNum || customer?.customerCompanyPhoneNumebr || "";
      const sales: any[] = Array.isArray(inv?.quoteWorkOrderSale) ? inv.quoteWorkOrderSale : [];

      sales.forEach((sale, sIdx) => {
        const firstPickup = Array.isArray(sale?.pickups) && sale.pickups.length > 0 ? sale.pickups[0] : null;
        const lastDropoff = Array.isArray(sale?.dropoffs) && sale.dropoffs.length > 0
          ? sale.dropoffs[sale.dropoffs.length - 1] : null;
        const pickupDt = firstPickup?.dateTime || sale?.departureDateTime;
        if (!pickupDt) return;
        const fromAddr = firstPickup?.address
          ? `${firstPickup.address}${firstPickup.city ? `, ${firstPickup.city}` : ""}`
          : (sale?.transferTypeFrom || "—");
        const toAddr = lastDropoff?.address
          ? `${lastDropoff.address}${lastDropoff.city ? `, ${lastDropoff.city}` : ""}`
          : (sale?.transferTypeTo || "—");

        allTrips.push({
          id: `${inv.id}-${sIdx}`,
          workOrderId: String(inv?.work_order_id ?? inv?.id ?? ""),
          pickupDateTime: pickupDt,
          dropoffDateTime: lastDropoff?.dateTime ?? sale?.returnDateTime ?? null,
          clientName,
          clientPhone,
          from: fromAddr,
          to: toAddr,
          status: "Assigned",
        });
      });
    });

    allTrips.sort((a, b) => moment(a.pickupDateTime).valueOf() - moment(b.pickupDateTime).valueOf());

    const now = moment();
    const todayKey = now.format("YYYY-MM-DD");
    const todayTrips = allTrips.filter((t) => moment(t.pickupDateTime).format("YYYY-MM-DD") === todayKey);
    const upcomingTrips = allTrips
      .filter((t) => moment(t.pickupDateTime).isAfter(now.clone().endOf("day")))
      .slice(0, 5);
    const nextTrip = [...todayTrips, ...upcomingTrips].find((t) => moment(t.pickupDateTime).isAfter(now)) || null;

    const fmtCountdown = (dt: string): string => {
      const target = moment(dt);
      const totalMin = target.diff(now, "minutes");
      if (totalMin <= 0) return "Now";
      const days = Math.floor(totalMin / (60 * 24));
      const hours = Math.floor((totalMin - days * 60 * 24) / 60);
      const mins = totalMin - days * 60 * 24 - hours * 60;
      if (days > 0) return `${days}d ${hours}h`;
      if (hours > 0) return `${hours}h ${mins}m`;
      return `${mins} min`;
    };

    const mapsUrl = (from: string, to: string) =>
      `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(from)}&destination=${encodeURIComponent(to)}`;

    // Alerts — derived from the same trip list:
    //   • Soon: today's trip starting within the next 60 minutes.
    //   • New: trip created in the last 24h (assigned recently).
    //   • Time-shift: any trip where the local "lastSeen" record disagrees.
    //     Detection is best-effort — uses localStorage as a per-driver cache
    //     keyed on tripId so admins editing pickup time surface to the driver.
    const alerts: { kind: "soon" | "new" | "time"; text: string; trip: DriverTrip }[] = [];
    todayTrips.forEach((t) => {
      const diff = moment(t.pickupDateTime).diff(now, "minutes");
      if (diff > 0 && diff <= 60) {
        alerts.push({ kind: "soon", text: `${t.clientName} pickup in ${diff} min`, trip: t });
      }
    });
    try {
      const cacheKey = `driverTripTimes:${userInfo?.id ?? "self"}`;
      const cached = JSON.parse(localStorage.getItem(cacheKey) || "{}") as Record<string, string>;
      const next: Record<string, string> = {};
      allTrips.forEach((t) => {
        next[t.id] = t.pickupDateTime;
        if (cached[t.id] && cached[t.id] !== t.pickupDateTime) {
          alerts.push({
            kind: "time",
            text: `Pickup time changed for ${t.clientName} (${moment(t.pickupDateTime).format("MMM D, h:mm A")})`,
            trip: t,
          });
        } else if (!cached[t.id]) {
          alerts.push({ kind: "new", text: `New trip assigned: ${t.clientName} on ${moment(t.pickupDateTime).format("MMM D, h:mm A")}`, trip: t });
        }
      });
      localStorage.setItem(cacheKey, JSON.stringify(next));
    } catch { /* localStorage unavailable — skip diffing */ }

    const TripCard: React.FC<{ trip: DriverTrip }> = ({ trip }) => {
      const pickup = moment(trip.pickupDateTime);
      const dropoff = trip.dropoffDateTime ? moment(trip.dropoffDateTime) : null;
      return (
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 2.5,
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "stretch" } }}>
            {/* Time block */}
            <Box sx={{ bgcolor: "#eef2ff", px: 2.5, py: 2, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minWidth: { sm: 130 } }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#4338ca", textTransform: "uppercase", letterSpacing: 0.6 }}>
                Pickup
              </Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#1e293b", lineHeight: 1.1, mt: 0.5 }}>
                {pickup.format("h:mm A")}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.25 }}>
                {pickup.format("ddd, MMM D")}
              </Typography>
              {dropoff && dropoff.isAfter(pickup) && (
                <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 0.75 }}>
                  Drop-off {dropoff.format("h:mm A")}
                </Typography>
              )}
            </Box>

            {/* Body */}
            <Box sx={{ flex: 1, p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1, mb: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                  {trip.clientName}
                </Typography>
                <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: "#dcfce7", border: "1px solid #86efac" }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#15803d" }}>
                    {trip.status}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
                <LocationOnIcon sx={{ fontSize: 14, color: "#16a34a", mt: 0.25 }} />
                <Typography sx={{ fontSize: 12.5, color: "#1e293b" }}>{trip.from}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
                <LocationOnIcon sx={{ fontSize: 14, color: "#dc2626", mt: 0.25 }} />
                <Typography sx={{ fontSize: 12.5, color: "#1e293b" }}>{trip.to}</Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                  onClick={() => navigate(`/driverBooking/list?trip=${encodeURIComponent(trip.id)}`)}
                  sx={{ textTransform: "none", borderRadius: 2, fontSize: 12, fontWeight: 600, borderColor: "#e2e8f0", color: "#1e293b", "&:hover": { bgcolor: "#f8fafc", borderColor: "#94a3b8" } }}
                >
                  View Details
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MapIcon sx={{ fontSize: 14 }} />}
                  onClick={() => window.open(mapsUrl(trip.from, trip.to), "_blank", "noopener,noreferrer")}
                  sx={{ textTransform: "none", borderRadius: 2, fontSize: 12, fontWeight: 600, borderColor: "#bfdbfe", color: "#1d4ed8", "&:hover": { bgcolor: "#eff6ff", borderColor: "#3b82f6" } }}
                >
                  Open Map
                </Button>
                {trip.clientPhone && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PhoneIcon sx={{ fontSize: 14 }} />}
                    onClick={() => { window.location.href = `tel:${trip.clientPhone}`; }}
                    sx={{ textTransform: "none", borderRadius: 2, fontSize: 12, fontWeight: 700, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}
                  >
                    Call Client
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      );
    };

    return (
      <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", p: { xs: 1.5, md: 3 } }}>
        {/* Date / greeting */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
            My Day
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25 }}>
            {now.format("dddd, MMMM D, YYYY")}
          </Typography>
        </Box>

        {/* ── Section 1: Status header ── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 1.5,
            mb: 3,
          }}
        >
          <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: "#2f3891", color: "#fff" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.85 }}>
              Trips Today
            </Typography>
            <Typography sx={{ fontSize: 32, fontWeight: 800, lineHeight: 1, mt: 0.75 }}>
              {todayTrips.length}
            </Typography>
            <Typography sx={{ fontSize: 11, opacity: 0.85, mt: 0.5 }}>
              {todayTrips.length === 0 ? "No trips scheduled" : "Scheduled for today"}
            </Typography>
          </Box>
          <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: "#fff", border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>
              Next Trip
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#1e293b", lineHeight: 1.1, mt: 0.75 }}>
              {nextTrip ? moment(nextTrip.pickupDateTime).format("h:mm A") : "—"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
              {nextTrip
                ? `${moment(nextTrip.pickupDateTime).format("ddd, MMM D")} · ${nextTrip.clientName}`
                : "Nothing on the schedule"}
            </Typography>
          </Box>
          <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: "#fff", border: "1px solid #e2e8f0", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <AccessTimeIcon sx={{ fontSize: 14, color: "#0ea5e9" }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>
                Countdown
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: nextTrip ? "#0ea5e9" : "#94a3b8", lineHeight: 1.1, mt: 0.75 }}>
              {nextTrip ? fmtCountdown(nextTrip.pickupDateTime) : "—"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
              Until next pickup
            </Typography>
          </Box>
        </Box>

        {/* ── Section 4 (top placement when present): Alerts ── */}
        {alerts.length > 0 && (
          <Box sx={{ mb: 3, borderRadius: 2.5, bgcolor: "#fffbeb", border: "1px solid #fde68a", overflow: "hidden" }}>
            <Box sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #fde68a" }}>
              <NotificationsActiveIcon sx={{ fontSize: 16, color: "#b45309" }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#b45309", textTransform: "uppercase", letterSpacing: 0.6 }}>
                Alerts
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {alerts.map((a, i) => (
                <Box
                  key={i}
                  sx={{
                    px: 2, py: 1, display: "flex", alignItems: "center", gap: 1,
                    borderTop: i === 0 ? "none" : "1px solid #fef3c7",
                  }}
                >
                  <Box sx={{
                    width: 8, height: 8, borderRadius: "50%",
                    bgcolor: a.kind === "soon" ? "#dc2626" : a.kind === "time" ? "#d97706" : "#2563eb",
                  }} />
                  <Typography sx={{ fontSize: 12.5, color: "#1e293b" }}>{a.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── Section 2: Today's trips ── */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
            <DirectionsBusIcon sx={{ fontSize: 18, color: "#2f3891" }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.6 }}>
              Today's Trips
            </Typography>
          </Box>
          {todayTrips.length === 0 ? (
            <Box sx={{ p: 3, borderRadius: 2.5, bgcolor: "#fff", border: "2px dashed #e2e8f0", textAlign: "center" }}>
              <DirectionsBusIcon sx={{ fontSize: 36, color: "#cbd5e1", mb: 1 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#64748b" }}>
                No trips scheduled today
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                Enjoy the time off — upcoming trips appear below.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {todayTrips.map((t) => <TripCard key={t.id} trip={t} />)}
            </Box>
          )}
        </Box>

        {/* ── Section 3: Upcoming trips ── */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
            <EventIcon sx={{ fontSize: 18, color: "#2f3891" }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.6 }}>
              Upcoming Trips
            </Typography>
            {upcomingTrips.length > 0 && (
              <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: "#eef2ff", border: "1px solid #c7d2fe" }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#4338ca" }}>
                  Next {upcomingTrips.length}
                </Typography>
              </Box>
            )}
          </Box>
          {upcomingTrips.length === 0 ? (
            <Box sx={{ p: 3, borderRadius: 2.5, bgcolor: "#fff", border: "2px dashed #e2e8f0", textAlign: "center" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#64748b" }}>
                No upcoming trips
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {upcomingTrips.map((t) => <TripCard key={t.id} trip={t} />)}
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="text"
            onClick={() => navigate("/driverBooking/list")}
            sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 700, color: "#2f3891" }}
          >
            See full calendar →
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", p: { xs: 2, md: 3 } }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
            Dashboard
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25 }}>
            {new Date().toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: "America/Toronto" })}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {hasPermission("quotes", "can_create") && (
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/quote/create")}
              sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" }, fontSize: 12 }}>
              New Quote
            </Button>
          )}
          {hasPermission("invoices", "can_create") && (
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => navigate("/quote/create?asInvoice=true")}
              sx={{ textTransform: "none", borderRadius: 2, borderColor: "#2f3891", color: "#2f3891", fontSize: 12 }}>
              New Invoice
            </Button>
          )}
          {can("reports") && (
            <Button size="small" variant="outlined" startIcon={<ReceiptLongIcon />} onClick={() => navigate("/reports/balance-sheet")}
              sx={{ textTransform: "none", borderRadius: 2, borderColor: "#10b981", color: "#10b981", fontSize: 12 }}>
              Balance Sheet
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Smart Insights Bar ── */}
      {insights.length > 0 && (
      <Box
        sx={{
          bgcolor: "#1e293b", borderRadius: 2.5, px: 2.5, py: 1.25, mb: 3,
          display: "flex", alignItems: "center", gap: 2,
          boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#10b981", flexShrink: 0, boxShadow: "0 0 0 3px rgba(16,185,129,0.3)" }} />
        <Typography
          key={insightIdx}
          sx={{
            color: "#e2e8f0", fontSize: 13, fontWeight: 500,
            animation: "fadeSlide 0.4s ease",
            "@keyframes fadeSlide": { from: { opacity: 0, transform: "translateY(6px)" }, to: { opacity: 1, transform: "translateY(0)" } },
          }}
        >
          {insights[insightIdx]}
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
          {insights.map((_, i) => (
            <Box key={i} onClick={() => setInsightIdx(i)} sx={{ width: i === insightIdx ? 16 : 5, height: 5, borderRadius: 3, bgcolor: i === insightIdx ? "#2f3891" : "rgba(255,255,255,0.2)", cursor: "pointer", transition: "all 0.3s" }} />
          ))}
        </Box>
      </Box>
      )}

      {/* ── KPI Cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {([
          can("reports")   && { title: "Total Revenue", value: fmt(totalRevenue), icon: <TrendingUpIcon sx={{ fontSize: 80 }} />, gradient: ["#2f3891","#2f3891"], sparkData: monthlyPayments, onClick: () => navigate("/reports/balance-sheet") },
          can("reports")   && { title: "Total Paid", value: fmt(totalPaid), icon: <CheckCircleOutlineIcon sx={{ fontSize: 80 }} />, gradient: ["#10b981","#059669"], sparkData: monthlyPayments.map((v) => v * 0.8), onClick: () => navigate("/reports/balance-sheet") },
          can("invoices")  && { title: "Outstanding", value: fmt(outstanding), icon: <ErrorOutlineIcon sx={{ fontSize: 80 }} />, gradient: outstanding > 0 ? ["#f59e0b","#d97706"] : ["#10b981","#059669"], sparkData: monthlyInvoiceCounts, onClick: () => navigate("/invoice/list") },
          can("invoices")  && { title: "Total Invoices", value: String(totalInvoices), icon: <ReceiptLongIcon sx={{ fontSize: 80 }} />, gradient: ["#3b82f6","#2563eb"], sparkData: monthlyInvoiceCounts, onClick: () => navigate("/invoice/list") },
          can("customers") && { title: "Total Clients", value: String(totalClients), icon: <PeopleAltIcon sx={{ fontSize: 80 }} />, gradient: ["#ec4899","#db2777"], sparkData: Array(12).fill(totalClients), onClick: () => navigate("/customer/list") },
          can("trips")     && { title: "Today's Trips", value: String(todaysBookings.length), icon: <DirectionsBusIcon sx={{ fontSize: 80 }} />, gradient: ["#14b8a6","#0d9488"], sparkData: monthlyInvoiceCounts, onClick: () => navigate("/driverBooking/list") },
        ].filter(Boolean) as KpiProps[]).map((card, i) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={i} sx={{ display: "flex" }}>
            <KpiCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* ── Charts Row (requires reports or invoices permission) ── */}
      {(can("reports") || can("invoices")) && (
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Box sx={{ bgcolor: "#fff", borderRadius: 3, p: 2.5, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", height: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Revenue Trend</Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Monthly payments & invoice volume</Typography>
              </Box>
              <Chip label={new Date().getFullYear()} size="small" sx={{ bgcolor: "#f5f3ff", color: "#2f3891", fontWeight: 700, fontSize: 11 }} />
            </Box>
            <HighchartsReact highcharts={Highcharts} options={revenueChartOptions} />
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <Box sx={{ bgcolor: "#fff", borderRadius: 3, p: 2.5, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", height: "100%" }}>
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Payment Methods</Typography>
              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Revenue by payment type</Typography>
            </Box>
            {pieHasData ? (
              <HighchartsReact
                key={revenueByMethod.labels.join(",")}
                highcharts={Highcharts}
                options={methodChartOptions}
                immutable={false}
              />
            ) : (
              <Box sx={{ height: 240, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>No payment data available</Typography>
                <Typography sx={{ fontSize: 11, mt: 0.5 }}>Payments will appear here once recorded</Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      )}

      {/* ── Bottom Row ── */}
      <Grid container spacing={2.5}>

        {/* Today's Trips — visible if user can view trips */}
        {can("trips") && (
        <Grid item xs={12} md={5}>
          <Box sx={{ bgcolor: "#fff", borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}>

            {/* Header */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <DirectionsBusIcon sx={{ fontSize: 17, color: "#14b8a6" }} />
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Today's Trips</Typography>
              <Box sx={{ px: 1, py: 0.2, borderRadius: 10, bgcolor: "#ccfbf1" }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#0d9488" }}>{todaysBookings.length}</Typography>
              </Box>
              <Box sx={{ ml: "auto", display: "flex", gap: 0.75, alignItems: "center" }}>
                {selectedTrips.size > 0 && (
                  <Button size="small" variant="contained" onClick={handleBulkSmsDrivers} disabled={bulkSending}
                    startIcon={bulkSending ? <CircularProgress size={11} color="inherit" /> : <SmsIcon sx={{ fontSize: 13 }} />}
                    sx={{ textTransform: "none", fontSize: 11, py: 0.4, px: 1.25, borderRadius: 1.5, bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" } }}>
                    SMS {selectedTrips.size} Driver{selectedTrips.size > 1 ? "s" : ""}
                  </Button>
                )}
                {can("invoices") && <Typography onClick={() => navigate("/invoice/list")} sx={{ fontSize: 11, color: "#2f3891", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>View all →</Typography>}
              </Box>
            </Box>

            {/* Body */}
            {todaysBookings.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <DirectionsBusIcon sx={{ fontSize: 40, color: "#e2e8f0", mb: 1 }} />
                <Typography sx={{ fontSize: 13, color: "#94a3b8", fontWeight: 500, mb: 2 }}>
                  No trips today — you're all caught up 🚀
                </Typography>
                <Button size="small" variant="outlined" onClick={() => navigate("/invoice/list")}
                  sx={{ textTransform: "none", fontSize: 12, borderRadius: 2, borderColor: "#2f3891", color: "#2f3891" }}>
                  View Upcoming Trips
                </Button>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
                {todaysBookings.map((booking, i) => {
                  const { client, time, pickup, dropoff, drivers, driverNames } = getTripInfo(booking);
                  const clientSent = smsSent.has(`${booking.work_order_id}_client`);
                  const driverSent = smsSent.has(`${booking.work_order_id}_driver`);
                  const isSelected = selectedTrips.has(booking.work_order_id);
                  const status = booking.quoteWorkOrderStatus;
                  const statusLabel = status === 0 ? "Paid" : status === 2 ? "Invoiced" : "Pending";
                  const statusColor = status === 0 ? { bg: "#dcfce7", text: "#16a34a" } : status === 2 ? { bg: "#dbeafe", text: "#2563eb" } : { bg: "#fef9c3", text: "#b45309" };
                  const tripName = (booking as any).quoteTripName;
                  return (
                    <Box key={i} sx={{ px: 1.5, py: 1.25, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "flex-start", gap: 1, bgcolor: isSelected ? "#f5f3ff" : "transparent", transition: "background 0.15s", "&:hover": { bgcolor: isSelected ? "#f5f3ff" : "#fafafa" } }}>
                      {/* Checkbox */}
                      <Checkbox size="small" checked={isSelected} onChange={() => toggleTrip(booking.work_order_id)}
                        sx={{ p: 0.25, mt: 0.25, color: "#c4b5fd", "&.Mui-checked": { color: "#2f3891" } }} />

                      {/* Avatar */}
                      <Avatar sx={{ width: 32, height: 32, bgcolor: "#ccfbf1", fontSize: 12, fontWeight: 700, color: "#0d9488", flexShrink: 0, mt: 0.25 }}>
                        {client[0]?.toUpperCase() || "?"}
                      </Avatar>

                      {/* Trip details */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Client name + status badge */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.3 }}>
                          <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>{client}</Typography>
                          <Box sx={{ px: 0.75, py: 0.1, borderRadius: 5, bgcolor: statusColor.bg, flexShrink: 0 }}>
                            <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: statusColor.text, lineHeight: 1.5, textTransform: "uppercase", letterSpacing: 0.4 }}>{statusLabel}</Typography>
                          </Box>
                        </Box>

                        {/* Route */}
                        {(pickup !== "—" || dropoff !== "—") && (
                          <Typography noWrap sx={{ fontSize: 11, color: "#475569", fontWeight: 500, lineHeight: 1.4 }}>
                            📍 <Box component="span" sx={{ fontWeight: 600, color: "#334155" }}>{pickup}</Box>
                            {dropoff !== "—" && <Box component="span" sx={{ color: "#94a3b8" }}> → </Box>}
                            {dropoff !== "—" && <Box component="span" sx={{ fontWeight: 600, color: "#334155" }}>{dropoff}</Box>}
                          </Typography>
                        )}

                        {/* Time + Driver */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.25, flexWrap: "wrap" }}>
                          {time !== "—" && (
                            <Typography sx={{ fontSize: 10.5, color: "#64748b", display: "flex", alignItems: "center", gap: 0.3 }}>
                              🕒 {time}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: 10.5, color: driverNames !== "—" ? "#64748b" : "#cbd5e1" }}>
                            🚍 {driverNames !== "—" ? driverNames : "No driver assigned"}
                          </Typography>
                          {tripName && (
                            <Typography noWrap sx={{ fontSize: 10, color: "#94a3b8", fontStyle: "italic" }}>
                              {tripName}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Action buttons */}
                      <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0, mt: 0.25 }}>
                        <Tooltip title="Print trip" arrow>
                          <IconButton size="small" onClick={() => setPrintTrip(booking)} sx={{ p: 0.5, color: "#64748b", "&:hover": { color: "#0f172a", bgcolor: "#f1f5f9" } }}>
                            <PrintIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={clientSent ? "Client SMS sent ✓" : "SMS Client"} arrow>
                          <IconButton size="small" onClick={() => openSmsModal(booking, "client")}
                            sx={{ p: 0.5, color: clientSent ? "#10b981" : "#2f3891", "&:hover": { bgcolor: "#f5f3ff" } }}>
                            {clientSent ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <PersonIcon sx={{ fontSize: 14 }} />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={drivers.length === 0 ? "No driver assigned" : driverSent ? "Driver SMS sent ✓" : "SMS Driver"} arrow>
                          <span>
                            <IconButton size="small" disabled={drivers.length === 0} onClick={() => openSmsModal(booking, "driver")}
                              sx={{ p: 0.5, color: driverSent ? "#10b981" : "#14b8a6", "&:hover": { bgcolor: "#f0fdfa" }, "&.Mui-disabled": { color: "#e2e8f0" } }}>
                              {driverSent ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <SmsIcon sx={{ fontSize: 14 }} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Open details" arrow>
                          <IconButton size="small" onClick={() => navigate(`/work-order/history/${booking.work_order_id}`)}
                            sx={{ p: 0.5, color: "#94a3b8", "&:hover": { color: "#0f172a", bgcolor: "#f1f5f9" } }}>
                            <DescriptionIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Grid>
        )}

        {/* Recent Payments */}
        {can("invoices") && (
        <Grid item xs={12} md={4}>
          <Box sx={{ bgcolor: "#fff", borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}>
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 1 }}>
              <AccountBalanceWalletIcon sx={{ fontSize: 18, color: "#2f3891" }} />
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Recent Payments</Typography>
            </Box>
            {recentPayments.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <AccountBalanceWalletIcon sx={{ fontSize: 40, color: "#e2e8f0", mb: 1 }} />
                <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No payments yet</Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 310, overflowY: "auto" }}>
                {recentPayments.map((p, i) => (
                  <Box key={i} sx={{ px: 2.5, py: 1.25, borderBottom: "1px solid #f8fafc", display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.is_return ? "#ef4444" : "#10b981", flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                        {p.InvoiceNo || `Payment #${p.id}`}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                        {new Date(p.created_at).toLocaleDateString("en-CA")} · {p.payment_method}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: p.is_return ? "#ef4444" : "#10b981" }}>
                      {p.is_return ? "-" : "+"}${Number(p.amount).toFixed(2)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Grid>
        )}

        {/* Pending Deposits */}
        {can("deposits") && (
        <Grid item xs={12} md={3}>
          <Box sx={{ bgcolor: "#fff", borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}>
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: pendingDeposits.length > 0 ? "#f59e0b" : "#10b981" }} />
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Pending Deposits</Typography>
              {pendingDeposits.length > 0 && (
                <Box sx={{ ml: "auto", px: 1, py: 0.2, borderRadius: 10, bgcolor: "#fef3c7" }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#d97706" }}>{pendingDeposits.length}</Typography>
                </Box>
              )}
            </Box>
            {pendingDeposits.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "#10b981", mb: 1 }} />
                <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>All deposits approved ✓</Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 310, overflowY: "auto" }}>
                {pendingDeposits.map((d, i) => (
                  <Box key={i} sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #f8fafc" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                        {fmtDriverName(d.driver)}
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                        ${Number(d.amount).toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                        {new Date(d.created_at).toLocaleDateString("en-CA")}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleApproveDeposit(d.id)}
                        disabled={approving === d.id}
                        sx={{ textTransform: "none", fontSize: 10, py: 0.25, px: 1.25, borderRadius: 1.5, bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" }, minWidth: 0 }}
                      >
                        {approving === d.id ? <CircularProgress size={10} color="inherit" /> : "Approve"}
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Grid>
        )}

      </Grid>

      {/* ── Invoice status summary ── */}
      {can("invoices") && (
      <Box sx={{ mt: 2.5, bgcolor: "#fff", borderRadius: 3, p: 2.5, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a", mb: 2 }}>Invoice Status Summary</Typography>
        <Grid container spacing={2}>
          {[
            { label: "Paid", count: invoices.filter((i) => (Number(i.due_amount) || 0) === 0 && (Number(i.amount_paid) || 0) > 0).length, color: "#10b981", bg: "#f0fdf4", border: "#bbf7d0" },
            { label: "Outstanding", count: overdueCount, color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
            { label: "Total", count: totalInvoices, color: "#2f3891", bg: "#f5f3ff", border: "#ddd6fe" },
          ].map((s) => (
            <Grid item xs={4} key={s.label}>
              <Box sx={{ textAlign: "center", p: 2, borderRadius: 2.5, bgcolor: s.bg, border: `1px solid ${s.border}` }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.count}</Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: s.color, textTransform: "uppercase", letterSpacing: 0.6 }}>{s.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
      )}

    </Box>

    {/* ── SMS Modal ── */}
    {(() => {
      const sm = smsModal;
      const isClient = sm?.type === "client";
      const recipientPhone = sm
        ? isClient
          ? getTripInfo(sm.booking).clientPhone || "No phone on file"
          : getTripInfo(sm.booking).drivers.map((d) => d.driverMobileNum).filter(Boolean).join(", ") || "No phone on file"
        : "";
      return (
        <Dialog open={!!sm} onClose={() => setSmsModal(null)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: isClient ? "#f5f3ff" : "#f0fdfa", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {isClient ? <PersonIcon sx={{ fontSize: 18, color: "#2f3891" }} /> : <SmsIcon sx={{ fontSize: 18, color: "#14b8a6" }} />}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>
                {isClient ? "SMS to Client" : "SMS to Driver"}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>To: {recipientPhone}</Typography>
            </Box>
            <IconButton onClick={() => setSmsModal(null)} size="small" sx={{ ml: "auto" }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <TextField
              fullWidth multiline rows={8} value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, fontFamily: "monospace" } }}
            />
            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.75 }}>
              {smsMessage.length} characters · Edit message before sending
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={() => setSmsModal(null)} variant="outlined"
              sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e2e8f0", color: "#64748b" }}>
              Cancel
            </Button>
            <Button onClick={handleSendSms} variant="contained" disabled={smsSending || !smsMessage.trim()}
              startIcon={smsSending ? <CircularProgress size={14} color="inherit" /> : <SmsIcon sx={{ fontSize: 15 }} />}
              sx={{ textTransform: "none", borderRadius: 2, bgcolor: isClient ? "#2f3891" : "#14b8a6", "&:hover": { bgcolor: isClient ? "#2f3891" : "#0d9488" } }}>
              {smsSending ? "Sending…" : "Send SMS"}
            </Button>
          </DialogActions>
        </Dialog>
      );
    })()}

    {/* ── Print Modal ── */}
    {(() => {
      const pt = printTrip;
      const info = pt ? getTripInfo(pt) : null;
      const grandTotal  = parseFloat(pt?.quoteWorkOrderSaleGrandTotal || "0");
      const subTotal    = parseFloat((pt as any)?.quoteWorkOrderSubTotal || "0");
      const discount    = parseFloat((pt as any)?.quoteWorkOrderDiscount || "0");
      const tax         = Math.max(0, grandTotal - subTotal + discount);
      const co          = pt?.company;
      const invoiceNo   = pt?.InvoiceNo || `#${pt?.id}`;
      const printDate   = new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
      const statusLabel = pt?.quoteWorkOrderStatus === 0 ? "PAID" : pt?.quoteWorkOrderStatus === 2 ? "INVOICED" : "CONFIRMED";
      const statusColor = pt?.quoteWorkOrderStatus === 0 ? "#16a34a" : pt?.quoteWorkOrderStatus === 2 ? "#2563eb" : "#2f3891";
      const statusBg    = pt?.quoteWorkOrderStatus === 0 ? "#dcfce7" : pt?.quoteWorkOrderStatus === 2 ? "#dbeafe" : "#ede9fe";

      return (
        <Dialog open={!!pt} onClose={() => setPrintTrip(null)} maxWidth="md" fullWidth
          PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh" } }}>

          {/* Modal toolbar (non-printable) */}
          <Box className="no-print" sx={{ px: 3, py: 1.5, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Trip Confirmation — Print Preview</Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button size="small" onClick={() => setPrintTrip(null)} variant="outlined"
                sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e2e8f0", color: "#64748b", fontSize: 12 }}>
                Close
              </Button>
              <Button size="small" onClick={() => window.print()} variant="contained" startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
                sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" }, fontSize: 12 }}>
                Print
              </Button>
            </Box>
          </Box>

          <DialogContent sx={{ p: 0, bgcolor: "#f8fafc" }}>
            {pt && info && (
              <Box id="trip-print-area" sx={{ m: { xs: 1.5, sm: 2.5 }, bgcolor: "#fff", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 12px rgba(0,0,0,0.07)" }}>

                {/* ── HEADER ── */}
                <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", px: 3.5, pt: 3, pb: 2.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    {/* Brand */}
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.75 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: 1.5, bgcolor: "#2f3891", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <DirectionsBusIcon sx={{ fontSize: 22, color: "#fff" }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#fff", lineHeight: 1.1 }}>TJBus</Typography>
                          <Typography sx={{ fontSize: 10, color: "#94a3b8", letterSpacing: 0.5 }}>TRANSPORTATION SERVICES</Typography>
                        </Box>
                      </Box>
                      {co?.companyName && (
                        <Typography sx={{ fontSize: 11, color: "#cbd5e1", mt: 0.5 }}>{co.companyName}</Typography>
                      )}
                      {co?.companyMobileNumber && (
                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>📞 {co.companyMobileNumber}</Typography>
                      )}
                      {co?.companyEmail && (
                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>✉️ {co.companyEmail}</Typography>
                      )}
                    </Box>

                    {/* Document meta */}
                    <Box sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#fff", letterSpacing: 1, lineHeight: 1 }}>TRIP</Typography>
                      <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#2f3891", letterSpacing: 1 }}>CONFIRMATION</Typography>
                      <Box sx={{ mt: 1.25, bgcolor: "rgba(255,255,255,0.08)", borderRadius: 1.5, px: 1.5, py: 0.75 }}>
                        <Typography sx={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6 }}>Ref No</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{invoiceNo}</Typography>
                        <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.25 }}>{printDate}</Typography>
                      </Box>
                      {/* Status badge */}
                      <Box sx={{ mt: 1, display: "inline-block", px: 1.5, py: 0.4, borderRadius: 5, bgcolor: statusBg }}>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: statusColor, letterSpacing: 1 }}>{statusLabel}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* ── BODY ── */}
                <Box sx={{ px: 3.5, py: 3 }}>

                  {/* Client info card */}
                  <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 2, p: 2, mb: 3, display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <Box>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.7, mb: 0.3 }}>Client</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{info.client}</Typography>
                      {info.clientPhone && <Typography sx={{ fontSize: 11, color: "#64748b" }}>📞 {info.clientPhone}</Typography>}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.7, mb: 0.3 }}>Trip Date</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{info.date}</Typography>
                    </Box>
                    {info.passengers && (
                      <Box>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.7, mb: 0.3 }}>Passengers</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{info.passengers}</Typography>
                      </Box>
                    )}
                    {(pt as any).quoteTripName && (
                      <Box>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.7, mb: 0.3 }}>Trip Type</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{(pt as any).quoteTripName}</Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Route visual */}
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8, mb: 1.5 }}>Route Details</Typography>
                  <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                    {/* Pickup */}
                    <Box sx={{ flex: 1, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 2, p: 2, position: "relative" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#16a34a", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 10, fontWeight: 800, color: "#16a34a", textTransform: "uppercase", letterSpacing: 0.7 }}>Pickup</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", lineHeight: 1.4 }}>{info.pickup}</Typography>
                      {info.time !== "—" && (
                        <Typography sx={{ fontSize: 11, color: "#16a34a", fontWeight: 600, mt: 0.5 }}>🕒 {info.time}</Typography>
                      )}
                    </Box>

                    {/* Arrow */}
                    <Box sx={{ display: "flex", alignItems: "center", color: "#94a3b8", fontSize: 22, fontWeight: 300, flexShrink: 0 }}>→</Box>

                    {/* Drop-off */}
                    <Box sx={{ flex: 1, bgcolor: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 2, p: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#dc2626", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 10, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: 0.7 }}>Drop-off</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", lineHeight: 1.4 }}>{info.dropoff}</Typography>
                    </Box>
                  </Box>

                  {/* Driver */}
                  <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 2, p: 2, mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <PersonIcon sx={{ fontSize: 18, color: "#2f3891" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.7 }}>Assigned Driver</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: info.driverNames !== "—" ? "#0f172a" : "#94a3b8" }}>
                        {info.driverNames !== "—" ? info.driverNames : "Not yet assigned"}
                      </Typography>
                      {info.driverPhone && <Typography sx={{ fontSize: 11, color: "#64748b" }}>📞 {info.driverPhone}</Typography>}
                    </Box>
                  </Box>

                  {/* Pricing summary */}
                  <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 2, overflow: "hidden", mb: 3 }}>
                    <Box sx={{ bgcolor: "#f8fafc", px: 2.5, py: 1.25, borderBottom: "1px solid #e2e8f0" }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.7 }}>Pricing Summary</Typography>
                    </Box>
                    {[
                      { label: "Subtotal", value: `$${subTotal.toFixed(2)}` },
                      ...(discount > 0 ? [{ label: "Discount", value: `-$${discount.toFixed(2)}` }] : []),
                      ...(tax > 0 ? [{ label: "Tax", value: `$${tax.toFixed(2)}` }] : []),
                    ].map(({ label, value }) => (
                      <Box key={label} sx={{ display: "flex", justifyContent: "space-between", px: 2.5, py: 0.9, borderBottom: "1px solid #f1f5f9" }}>
                        <Typography sx={{ fontSize: 12, color: "#64748b" }}>{label}</Typography>
                        <Typography sx={{ fontSize: 12, color: "#0f172a" }}>{value}</Typography>
                      </Box>
                    ))}
                    {/* Total row */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", px: 2.5, py: 1.25, bgcolor: "#0f172a" }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>TOTAL</Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 900, color: "#fff" }}>${grandTotal.toFixed(2)}</Typography>
                    </Box>
                  </Box>

                  {/* Signature lines */}
                  <Box sx={{ display: "flex", gap: 3, mt: 1, mb: 1 }}>
                    {["Client Signature", "Driver Signature"].map((label) => (
                      <Box key={label} sx={{ flex: 1 }}>
                        <Box sx={{ height: 1, bgcolor: "#cbd5e1", mb: 0.75 }} />
                        <Typography sx={{ fontSize: 10, color: "#94a3b8", textAlign: "center" }}>{label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* ── FOOTER ── */}
                <Box sx={{ px: 3.5, py: 1.75, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }}>Thank you for choosing TJBus Transportation.</Typography>
                  <Typography sx={{ fontSize: 10, color: "#cbd5e1" }}>Generated {printDate}</Typography>
                </Box>

              </Box>
            )}
          </DialogContent>

          {/* Print CSS */}
          <style>{`
            @media print {
              body * { visibility: hidden; }
              #trip-print-area, #trip-print-area * { visibility: visible; }
              #trip-print-area { position: fixed; top: 0; left: 0; width: 100%; margin: 0; box-shadow: none !important; border-radius: 0 !important; }
              .no-print { display: none !important; }
            }
          `}</style>

        </Dialog>
      );
    })()}

    </>
  );
};

export default DashboardList;
